import type { GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box, useMediaQuery } from '@mui/material';
import { styled, Theme, useTheme } from '@mui/material/styles';

import { MetaHead } from 'components/MetaHead/MetaHead';
import { HeaderWithOptions } from 'components/Header';
import { Footer } from 'components/Footer';
import { PartnerBanks } from 'components/PartnerBanks/PartnerBanks';
import Advantages from 'components/Advantages';
import BottomNavigationMap from 'components/BottomNavigation/BottomNavigationMap';
import CTAStepExplanations from 'components/CTAStepExplanations';
import { TrustBanner } from 'components/TrustBanners/TrustBanner';
import { Divider } from 'components/Divider';
import { advantages } from 'legacy/data/advantages';
import { useDecision, withOptimizelyProps } from 'lib/optimizely';
import { Stage } from 'components/Stage/Stage';
import { ListIcons, SellingPointsPosition } from 'components/Stage/utils';
import { getStyleOverrides } from 'components/Stage/StyleOverrides/SEAPages';
import { LINKED_DATA_SCHEMA } from 'lib/constants/jsonld';
import { LegalTextGoogleFooter } from 'components/Stage/LegalText';
import { LegalTextGoogle } from 'components/Stage/LegalTextGoogle';
import { ABTestKeys, BLACK_FRIDAY_FLAG } from 'lib/optimizely/flagConfigs';

const StageBackGround = styled('div')(({ theme }) => ({
  background: `linear-gradient(0deg, #f7f7f7 0%, rgba(247, 247, 247, 0) 100%)`,
  padding: `${theme.spacing(3)} 0 ${theme.spacing(2)}`,
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(8)} 0 ${theme.spacing(4)}`,
  },
}));

const LandingPage = () => {
  const { pathname } = useRouter();
  const canonicalUrl = `https://www.smava.de${pathname}`;
  const theme = useTheme();
  const isMobile = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down('md'),
    { noSsr: true },
  );

  const [decision] = useDecision(ABTestKeys.CAT3106_Black_friday_banner);

  const showBlackFridayBadge = decision.variationKey === BLACK_FRIDAY_FLAG;

  // Default case - do not touch! Do not add AB tests here under any circumstances.
  return (
    <>
      <MetaHead
        follow={true}
        index={true}
        markupSchema={LINKED_DATA_SCHEMA.website}
      />
      <Head>
        {process.env.RELEASE_STAGE === 'live' && (
          <link href={canonicalUrl} rel="canonical" />
        )}
      </Head>

      <HeaderWithOptions
        hasLoginLink={false}
        hasNavigationMenu={false}
        hasPhoneBox={false}
      />
      <StageBackGround>
        <Stage
          sellingPoint1="Persönliche Angebote von über 20 Banken"
          sellingPoint2="SCHUFA-neutral und 100% kostenlos"
          sellingPoint3="Deutschlands bekanntestes** Kreditportal"
          sellingPointsPosition={SellingPointsPosition.UP}
          listIcon={ListIcons.CHECK_MARKS}
          showFeaturedPanel
          showGuaranteeBadgeOnTop
          {...(showBlackFridayBadge && { showBlackFridayBadge })}
          styleOverrides={getStyleOverrides(theme)}
          isBulletPointsPositionRearranged
          isCreditCardCategoryEnabled
        />
        {isMobile && <LegalTextGoogle />}
      </StageBackGround>
      <PartnerBanks />
      {!isMobile && <LegalTextGoogle />}
      <TrustBanner title="Über 300.000 zufriedene Kunden seit 2007" />

      {/* @ts-ignore */}
      <Advantages {...advantages} />
      <CTAStepExplanations headline="So einfach geht's" />
      <LegalTextGoogleFooter />
      <Box pb={2}>
        <Divider />
      </Box>
      <BottomNavigationMap />
      <Footer />
    </>
  );
};

// We needed to switch this to server side rendering as the
// context would otherwise be buggy. This might be a nextjs issue
// export const getStaticProps = async (): Promise<
//   GetStaticPropsResult<PageProps>
// > => {

export const getServerSideProps = withOptimizelyProps(
  async (): Promise<GetServerSidePropsResult<{}>> => {
    return {
      props: {},
      // revalidate: 3600, // 1 hour
    };
  },
);

export default LandingPage;
